// Library imports
import React, { useState } from "react";

// User Component imports
import { ProgressBar } from "_core/components/common/UI";
import OpsDialog from "../common/OpsDialog";
import { useVideoAttachments } from "./useVideoAttachments";
import { useSubmitWhenReady, calcOptimizeProgress } from "./useSubmitWhenReady";
import { VIDEO_SLOTS, REQUEST_SIZE_LIMIT, isVoiceSelectable, voiceOptionLabel } from "./appearanceDefs";
import VideoSlotRow from "./VideoSlotRow";

// 名称の最大文字数（t_appearance.name）
const NAME_MAX_LENGTH = 64;

// 未保存の入力を失わないよう、背景クリックでは閉じない
const IgnoreBackdropClick = () => {};

/**
 * 外見動画の追加。idle / talk で外見を作り、任意の動画はその後1本ずつ追加する（1リクエスト100MBの上限のため）
 */
const AppearanceAddModal = ({ show, voices, maxFileSize, onClose, onSubmit, onPreviewVoice, progress }) => {
    const [name, setName] = useState("");
    const [voiceId, setVoiceId] = useState("");
    const { files, attach, clear, pick, setOptimize, autoMini, setAutoMini, busy, hasError } = useVideoAttachments();
    // idle の行に常に出す。自分で添付したウィジェット表示動画があるときは出さず、idle が WebM なら作れないので押せなくする
    const ownMini = files.idle_mini && !files.idle_mini.auto;
    const miniBlocked = !!files.idle && !files.idle.canOptimize;
    const miniToggle = ownMini ? null : { checked: autoMini && !miniBlocked, disabled: miniBlocked, onChange: setAutoMini };
    const isUploading = progress !== null;

    const trimmedName = name.trim();
    const attached = Object.values(files);
    const hasBad = attached.some((f) => f.bad);
    const tooLarge = attached.some((f) => f.size > maxFileSize);
    const requiredSize = (files.idle?.size || 0) + (files.talk?.size || 0);
    const overRequest = requiredSize > REQUEST_SIZE_LIMIT;
    // 最適化中の動画はまだ file が無いので、添付されていれば送信を予約できる
    const hasRequired = files.idle && files.talk;
    const canSubmit = !!(trimmedName && trimmedName.length <= NAME_MAX_LENGTH && voiceId && hasRequired && !hasBad && !tooLarge && !overRequest && !hasError && !isUploading);
    const { reserved, request, cancel } = useSubmitWhenReady({
        busy, hasError, canSubmit,
        submit: () => onSubmit({ name: trimmedName, voiceId: Number(voiceId), files }),
    });
    const locked = isUploading || reserved;
    const waiting = calcOptimizeProgress(files);

    const footer = (
        <>
            {locked &&
                <div className="ops-upload-progress">
                    <ProgressBar progress={isUploading ? progress : waiting.progress} subProgress={0} />
                    <span>
                        {!isUploading ? `最適化後に自動でアップロード（${waiting.done}/${waiting.total}）`
                            : progress < 1 ? `アップロード中 ${Math.round(progress * 100)}%` : "サーバーに保存中"}
                    </span>
                </div>
            }
            {!locked && <button type="button" className="ops-footer-btn" onClick={onClose}>キャンセル</button>}
            {reserved ?
                <button type="button" className="ops-footer-btn is-primary-outline" onClick={cancel}>自動アップロードを取り消す</button>
                :
                <button type="button" className="ops-footer-btn is-primary ops-upload-submit" disabled={!canSubmit} onClick={request}
                    title={busy ? "最適化が終わったら自動でアップロードします" : undefined}>
                    {busy ? "最適化後にアップロード" : "アップロード実行"}
                </button>
            }
        </>
    );

    return (
        <OpsDialog show={show} className="ops-scope" title="外見動画を追加" width={960} footer={footer}
            showClose={!locked} onClose={() => !locked && onClose()} onBackdropClick={IgnoreBackdropClick}>
            <div className="ops-form-card is-roomy is-edit">
                <div className="ops-edit-basic">
                    <div className="ops-form-field">
                        <div className="ops-field-title-row">
                            <span className="ops-field-title">名前</span>
                            <span className="ops-required-mark">※必須</span>
                        </div>
                        <input className="ops-input" value={name} maxLength={NAME_MAX_LENGTH} disabled={locked} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="ops-form-field">
                        <div className="ops-field-title-row">
                            <span className="ops-field-title">ボイス</span>
                            <span className="ops-required-mark">※必須</span>
                        </div>
                        <div className="ops-voice-row">
                            <select className="ops-select" value={voiceId} disabled={locked} onChange={(e) => setVoiceId(e.target.value)}>
                                <option value="">ボイスを選択…</option>
                                {voices.map((v) => <option key={v.id} value={v.id} disabled={!isVoiceSelectable(v)}>{voiceOptionLabel(v)}</option>)}
                            </select>
                            <button type="button" className="ops-voice-play" title="選択中のボイスをサンプルテキストで試聴します"
                                disabled={!voiceId} onClick={() => onPreviewVoice(Number(voiceId))}><i className="bi bi-play-fill" /></button>
                        </div>
                    </div>
                </div>
                <div className="ops-form-field">
                    <span className="ops-field-title">動画</span>
                    {(tooLarge || overRequest) &&
                        <div className="ops-error-text"><i className="bi bi-x-circle-fill" />
                            {tooLarge ? "1ファイル100MBまでです。" : "idle と talk の合計が100MBを超えています。"}ファイルを軽くしてください
                        </div>
                    }
                </div>
                <div className="ops-video-slots">
                    {VIDEO_SLOTS.map((slot) => (
                        <VideoSlotRow key={slot.key} mode="add" slot={slot} entry={files[slot.key]} disabled={locked}
                            onPick={() => pick(slot.key)} onDrop={(file) => attach(slot.key, file)} onClear={() => clear(slot.key)}
                            onToggleOptimize={(optimize) => setOptimize(slot.key, optimize)}
                            miniToggle={slot.key === "idle" ? miniToggle : null} miniLabel="ウィジェット表示動画も作る" />
                    ))}
                </div>
            </div>
        </OpsDialog>
    );
};

export default AppearanceAddModal;
